import { Role } from "@/enums/Role";
import DeviceUtility from "./device";
import StorageWrapper from "@/helpers/storage";

Number.prototype.isBetween = function (a, b) {
	const min = Math.min(a, b);
	const max = Math.max(a, b);
	return this >= min && this <= max;
};

export function safeGetItem(key) {
	try {
		return StorageWrapper.getItem(key);
	} catch (e) {
		console.error("Error accessing storage wrapper:", e);
		return null;
	}
}

export function safeSetItem(key, value) {
	try {
		StorageWrapper.setItem(key, value);
		return true;
	} catch (e) {
		console.error("Error setting storage wrapper item:", e);
		return false;
	}
}

export function beautifyName(name) {
	if (!name) {
		return "";
	}
	return name
		.split(" ")
		.filter((n) => n.length > 0)
		.map((n) => n[0].toUpperCase() + n.slice(1))
		.join(" ");
}

export function getCustomerDisplayName(record) {
	if (!record) {
		return "";
	}
	if (record.name && record.name.trim()) {
		return record.name;
	} else if (record.PhoneNumbers?.length > 0) {
		return record.PhoneNumbers[0].phoneNumber;
	} else if (record.EmailAddresses?.length > 0) {
		return record.EmailAddresses[0].emailAddress;
	}
	return "";
}

export function getUserDisplayName(record) {
	if (!record) {
		return "";
	}
	if (record.name && record.name.trim()) {
		return record.name;
	} else if (record.firstname && record.lastname) {
		return record.firstname + " " + record.lastname;
	} else if (record.phone) {
		return record.phone;
	} else if (record.email) {
		return record.email;
	} else if (record.PhoneNumbers && record.PhoneNumbers.length > 0) {
		return record.PhoneNumbers[0].phoneNumber;
	} else if (record.EmailAddresses && record.EmailAddresses.length > 0) {
		return record.EmailAddresses[0].emailAddress;
	}
	return "";
}

export function getUserAvatarBackgroundColor(comment) {
	return comment.agent ? comment.agent.color : getSeededAvatarColor(comment.User?.name ?? "");
}

export function setStartedByColumn(item, userId) {
	const x = item.User;
	if (!x) {
		return "";
	}
	let resp = x.firstname ? x.firstname + " " + x.lastname : x.email;
	if (x.id == userId) {
		resp += " (You)";
	}
	return resp;
}

export function getAgentNameInitials(agentName) {
	if (!agentName) return "-";
	const names = agentName.split(" ");
	if (names.length === 1) {
		return names[0].split("@")[0].substring(0, 2).toUpperCase();
	}
	return names
		.filter((x) => x.length > 0)
		.map((x) => x[0])
		.join("")
		.slice(0, 2)
		.toUpperCase();
}

export function getDisplayDate(comment) {
	return new Date(comment.createdAt).toLocaleDateString();
}

export function getSeededAvatarColor(name) {
	if (!name) return "primary";
	const avatarColors = [
		"#E6BB74",
		"#96B081",
		"#6B729D",
		"#798287",
		"#80736F",
		"#DAA56E",
		"#7AA17C",
		"#C0C285",
		"#7A6AA1",
		"#6A94BC",
		"#61A5AC",
	];
	const index = name.split(" ").reduce((acc, curr) => acc + curr.charCodeAt(0), 0) % avatarColors.length;
	return avatarColors[index];
}

export function sourceWithTimestamp(source, timestamp = 5) {
	return `${source}#t=${timestamp}`;
}

export function addEmailToCustomer(customer, emailAddress) {
	// If the customer's list of email addresses is empty, add the new email address.
	if (customer.EmailAddresses.length === 0) {
		customer.EmailAddresses.push({ emailAddress: emailAddress });
	}
	// If the email address is empty, remove it from the list.
	else if (emailAddress.length === 0) {
		customer.EmailAddresses.splice(0, 1);
	}
	// Otherwise, update the existing email address.
	else {
		customer.EmailAddresses[0] = customer.EmailAddresses[0] || {};
		customer.EmailAddresses[0].emailAddress = emailAddress;
	}

	return customer;
}

export function addPhoneNumberToCustomer(customer, phoneNumber) {
	if (!phoneNumber) {
		return customer;
	}
	// If the customer's list of phone numbers is empty, add the new phone number.
	if (customer.PhoneNumbers.length === 0) {
		customer.PhoneNumbers.push({ phoneNumber: phoneNumber });
	}
	// If the phone number is empty, remove it from the list.
	else if (phoneNumber.length === 0) {
		customer.PhoneNumbers.splice(0, 1);
	}
	// Otherwise, update the existing phone number.
	else {
		customer.PhoneNumbers[0] = customer.PhoneNumbers[0] || {};
		customer.PhoneNumbers[0].phoneNumber = phoneNumber;
	}

	return customer;
}

export function calculateTimeout(message) {
	if (!message) {
		console.warn("Message is empty, defaulting to 3200ms");
		return 3200;
	}
	return Math.min(Math.max(message.length * 50, 3200), 7000);
}

export function blurActiveElement() {
	if (document.activeElement) {
		try {
			document.activeElement.blur();
		} catch {
			// Best effort to blur from the active element.
		}
	}
}

export function debounce(func, wait) {
	let timeout;
	return function (...args) {
		clearTimeout(timeout);
		timeout = setTimeout(() => func.apply(this, args), wait);
	};
}

export function getSortedArrayCloneByStringKey(array, key) {
	if (!array || !key) return [];
	const arrClone = array.slice(0);
	return arrClone.sort((a, b) => a[key].localeCompare(b[key]));
}

export function getMessageFromError(error) {
	if (!error) return "Unknown error occurred.";

	// If error is a string, return it directly
	if (typeof error === 'string') return error;

	// If error has a message property, return that
	if (error.message && typeof error.message === 'string') return error.message;

	// If error has an error property, return that
	if (error.error && typeof error.error === 'string') return error.error;
	
	// If error has a response with data
	if (error?.response?.data) {
		const responseData = error.response.data;

		// If response data is a string
		if (typeof responseData === 'string') return responseData;

		// If response data has a message property
		if (responseData.message && typeof responseData.message === 'string') return responseData.message;

		// Try to stringify the response data
		try {
			return JSON.stringify(responseData);
		} catch (e) {
			// Fallback if stringify fails
			console.warn("Unable to stringify response data.", e);
			return "There was an issue with our API server. Please try again in a moment.";
		}
	}
}

export function isViewOnlyUser(me) {
	return me?.account?.roles?.length === 1 && me.account.roles[0].name === Role.ViewOnly;
}

/**
 * Toggles body scroll behavior to prevent background scrolling when modals/overlays are open. 
 * @param {boolean} stopBodyScroll - Whether to stop body scrolling
 *   - true: Disables body scroll (for modal open)
 *   - false: Restores normal body scroll (for modal close)
 */
export function toggleBodyStyles(stopBodyScroll) {
	if (stopBodyScroll) {
		document.body.style.overflow = 'hidden';
		document.body.style.position = 'fixed';
		document.body.style.width = '100%';
	} else {
		document.body.style.overflow = '';
		document.body.style.position = '';
		document.body.style.width = '';
	}
}